import { FC, useEffect, useState } from 'react';
import DayPicker, { PredefinedOption } from 'components/day-picker/DayPicker';
import { DownArrowIcon } from 'assets/icons';
import { UPON_RECEIPT_LABEL, useDueDateOptions } from 'containers/invoices/hooks/useDueDateOptions';
import { dateOptionMatcher } from '../util';
import DateUtils from 'utils/dateUtils';

interface DueDateSectionProps {
  currentDueDate: Date;
  invoiceDate: Date;
  onDueDateChange: (selectedDate: Date) => void;
}

export const DueDateSection: FC<DueDateSectionProps> = ({ currentDueDate, invoiceDate, onDueDateChange }) => {
  const [isCalendarDisabled, setIsCalendarDisabled] = useState(false);

  useEffect(() => {
    if (currentDueDate && invoiceDate && DateUtils.areSameDates(currentDueDate, invoiceDate)) {
      setIsCalendarDisabled(true);
    }
  }, []);

  // Use shared hook for predefined due date options
  const predefinedDueDateOptions = useDueDateOptions(invoiceDate);

  // Handler for predefined option selection
  const onPredefinedOptionSelect = (option: PredefinedOption) => {
    // Handle calendar disable state based on selected option
    if (option.label === UPON_RECEIPT_LABEL) {
      setIsCalendarDisabled(true);
    } else {
      setIsCalendarDisabled(false);
    }
  };

  const handleDueDateChange = (selectedDate: Date) => {
    onDueDateChange(DateUtils.getEndOfDay(selectedDate));

    if (dateOptionMatcher(predefinedDueDateOptions[0], selectedDate)) {
      setIsCalendarDisabled(true);
    } else {
      setIsCalendarDisabled(false);
    }
  };

  // Find the "Upon receipt" option and check if current date matches it
  const uponReceiptOption = predefinedDueDateOptions.find(option => option.label === UPON_RECEIPT_LABEL);
  const isUponReceipt = uponReceiptOption && dateOptionMatcher(uponReceiptOption, currentDueDate);

  // Display text logic
  const displayText = isUponReceipt
    ? `Due ${UPON_RECEIPT_LABEL?.toLowerCase()}`
    : DateUtils.getDateInFormat({
        date: currentDueDate,
        dateFormat: 'dd MMM yyyy'
      });

  // Button trigger component
  const buttonNode = (
    <div className="flex h-10 w-[226px] items-center justify-between rounded bg-secondaryBtn px-5 text-17px">
      <div className={'flex items-center text-primaryText'}>{displayText}</div>
      <DownArrowIcon />
    </div>
  );

  return (
    <div className="flex items-center gap-3">
      <div className="whitespace-nowrap text-17px font-semibold text-primaryText">Due date</div>
      <div className="z-10 w-full max-w-md">
        <DayPicker
          date={currentDueDate}
          onSelect={handleDueDateChange}
          predefinedOptions={predefinedDueDateOptions}
          onPredefinedOptionSelect={onPredefinedOptionSelect}
          optionMatcher={dateOptionMatcher}
          showPastDates={false}
          disableBeforeDate={invoiceDate}
          className="w-full"
          labelClassName="text-17px font-semibold"
          customTrigger={buttonNode}
          disableCalendar={isCalendarDisabled}
          position="right"
          popoverPanelStyle={{
            minWidth: '470px',
            top: '0px',
            boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)'
          }}
        />
      </div>
    </div>
  );
};
